import { useEffect, useRef } from 'react';

const CHECK_INTERVAL_MS = 5 * 60 * 1000;

function extractAssetPaths(html) {
  const out = new Set();
  const scriptRe = /<script[^>]+src="([^"]+)"/g;
  const linkRe = /<link[^>]+href="([^"]+\.css)"/g;
  let m;
  while ((m = scriptRe.exec(html))) {
    if (m[1].includes('/assets/')) out.add(m[1]);
  }
  while ((m = linkRe.exec(html))) {
    if (m[1].includes('/assets/')) out.add(m[1]);
  }
  return [...out].sort().join('|');
}

function getLoadedAssetSignature() {
  const out = new Set();
  for (const s of document.scripts) {
    const src = s.getAttribute('src');
    if (src && src.includes('/assets/')) out.add(src);
  }
  for (const l of document.querySelectorAll('link[rel=stylesheet]')) {
    const href = l.getAttribute('href');
    if (href && href.includes('/assets/')) out.add(href);
  }
  return [...out].sort().join('|');
}

export function useVersionCheck() {
  const loadedSignatureRef = useRef(getLoadedAssetSignature());
  const updatePendingRef = useRef(false);

  useEffect(() => {
    if (import.meta.env.DEV) return;
    if (!loadedSignatureRef.current) return;

    let cancelled = false;
    let intervalId = null;

    const triggerReload = () => {
      window.location.reload();
    };

    const reloadWhenHidden = () => {
      if (document.hidden) {
        triggerReload();
      } else {
        const onHidden = () => {
          if (document.hidden) {
            document.removeEventListener('visibilitychange', onHidden);
            triggerReload();
          }
        };
        document.addEventListener('visibilitychange', onHidden);
      }
    };

    const checkForUpdate = async () => {
      if (cancelled || updatePendingRef.current) return;
      try {
        const res = await fetch(`/?_v=${Date.now()}`, {
          cache: 'no-store',
          credentials: 'same-origin',
        });
        if (!res.ok) return;
        const html = await res.text();
        const liveSignature = extractAssetPaths(html);
        if (!liveSignature) return;
        if (liveSignature !== loadedSignatureRef.current) {
          updatePendingRef.current = true;
          if (intervalId) clearInterval(intervalId);
          reloadWhenHidden();
        }
      } catch {
        // network blip — try again next interval
      }
    };

    intervalId = setInterval(checkForUpdate, CHECK_INTERVAL_MS);

    const onVisible = () => {
      if (!document.hidden) checkForUpdate();
    };
    document.addEventListener('visibilitychange', onVisible);

    checkForUpdate();

    return () => {
      cancelled = true;
      if (intervalId) clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, []);
}
